import { createFileRoute, notFound } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { Award, ExternalLink, Mail, Phone, Globe, Linkedin, Github } from "lucide-react";

const loadPortfolio = createServerFn({ method: "GET" })
  .inputValidator((d: unknown) => z.object({ slug: z.string() }).parse(d))
  .handler(async ({ data }) => {
    const { createClient } = await import("@supabase/supabase-js");
    const supa = createClient(
      process.env.SUPABASE_URL!,
      process.env.SUPABASE_PUBLISHABLE_KEY!,
      { auth: { persistSession: false, autoRefreshToken: false, storage: undefined } },
    );
    const { data: settings } = await supa.from("portfolio_settings").select("*").eq("public_slug", data.slug).eq("is_public", true).maybeSingle();
    if (!settings) return null;
    const { data: learner } = await supa.from("learners").select("first_name, last_name").eq("id", settings.learner_id).maybeSingle();
    const { data: items } = await supa.from("portfolio_items").select("id, kind, title, description, issuer, date_awarded, date_start, date_end, location, url, skills, is_featured").eq("learner_id", settings.learner_id).eq("visibility", "public").order("date_awarded", { ascending: false, nullsFirst: false });
    return { settings, learner, items: items ?? [] };
  });

export const Route = createFileRoute("/p/$slug")({
  loader: async ({ params }) => {
    const result = await loadPortfolio({ data: { slug: params.slug } });
    if (!result) throw notFound();
    return result;
  },
  head: ({ loaderData }) => ({
    meta: [
      { title: loaderData ? `${loaderData.learner?.first_name ?? ""} ${loaderData.learner?.last_name ?? ""} — Portfolio` : "Portfolio" },
      { name: "description", content: loaderData?.settings.headline ?? "Student portfolio" },
    ],
  }),
  errorComponent: ({ error }) => <div className="p-10 text-center text-muted-foreground">{error.message}</div>,
  notFoundComponent: () => <div className="p-10 text-center text-muted-foreground">This portfolio is not public.</div>,
  component: PublicPortfolio,
});

const KIND_LABEL: Record<string, string> = {
  certificate: "Certificate", award: "Award", project: "Project", photo: "Photo",
  video: "Video", achievement: "Achievement", skill: "Skill", competition: "Competition",
  volunteer: "Volunteer", report_card: "Report card",
  university_application: "University", employment: "Employment",
};

function PublicPortfolio() {
  const { settings, learner, items } = Route.useLoaderData() as NonNullable<Awaited<ReturnType<typeof loadPortfolio>>>;
  const name = `${learner?.first_name ?? ""} ${learner?.last_name ?? ""}`.trim() || "Student Portfolio";
  const groups: Record<string, typeof items> = {};
  for (const it of items) (groups[it.kind] = groups[it.kind] ?? []).push(it);
  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto p-6 md:p-10">
        <header className="border-b pb-8 mb-8">
          <h1 className="text-4xl font-bold tracking-tight">{name}</h1>
          {settings.headline && <p className="text-lg text-muted-foreground mt-2">{settings.headline}</p>}
          {settings.bio && <p className="mt-4 leading-relaxed max-w-2xl">{settings.bio}</p>}
          <div className="mt-4 flex flex-wrap gap-3 text-sm text-muted-foreground">
            {settings.contact_email && <span className="inline-flex items-center gap-1"><Mail className="size-3.5" />{settings.contact_email}</span>}
            {settings.contact_phone && <span className="inline-flex items-center gap-1"><Phone className="size-3.5" />{settings.contact_phone}</span>}
            {settings.website && <a href={settings.website} className="inline-flex items-center gap-1 hover:text-primary" target="_blank" rel="noreferrer"><Globe className="size-3.5" />Website</a>}
            {settings.linkedin && <a href={settings.linkedin} className="inline-flex items-center gap-1 hover:text-primary" target="_blank" rel="noreferrer"><Linkedin className="size-3.5" />LinkedIn</a>}
            {settings.github && <a href={settings.github} className="inline-flex items-center gap-1 hover:text-primary" target="_blank" rel="noreferrer"><Github className="size-3.5" />GitHub</a>}
          </div>
        </header>

        {Object.keys(groups).length === 0 && (
          <p className="text-muted-foreground">No public items yet.</p>
        )}

        {Object.entries(groups).map(([kind, rows]) => (
          <section key={kind} className="mb-8">
            <h2 className="text-xs font-bold uppercase tracking-widest text-primary mb-3 flex items-center gap-2"><Award className="size-3.5" />{KIND_LABEL[kind] ?? kind}</h2>
            <div className="space-y-4">
              {rows.map((r) => (
                <article key={r.id} className="border-l-2 border-primary/40 pl-4">
                  <div className="flex flex-wrap items-baseline gap-2 justify-between">
                    <h3 className="font-semibold">{r.title}</h3>
                    <span className="text-xs text-muted-foreground">{r.date_awarded ?? r.date_end ?? r.date_start ?? ""}</span>
                  </div>
                  {(r.issuer || r.location) && <p className="text-sm text-muted-foreground">{[r.issuer, r.location].filter(Boolean).join(" • ")}</p>}
                  {r.description && <p className="text-sm mt-1 leading-relaxed">{r.description}</p>}
                  {r.skills && r.skills.length > 0 && <p className="text-xs text-muted-foreground mt-1">Skills: {r.skills.join(", ")}</p>}
                  {r.url && <a href={r.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-primary hover:underline mt-1"><ExternalLink className="size-3" />View</a>}
                </article>
              ))}
            </div>
          </section>
        ))}

        <footer className="border-t pt-4 mt-12 text-xs text-muted-foreground text-center">Powered by MAKAI</footer>
      </div>
    </div>
  );
}
